import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Grid from "@mui/material/Grid";
import store from "../../store";
import "./_index.scss";
import CloseIcon from "@mui/icons-material/Close";
import section from "./section";
import degree from "./degree";
import WeatherLoader from "../../components/loader";
import axios from "axios";

const drawerWidth = 0;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  height: "82vh",
  display: "flex",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface ImageModalProps {
  onClose: () => void;
  VideoUrl: any;
}

interface GifModalProps {
  onClose: () => void;
  SquallMap: any;
}

const ImageModal: React.FC<ImageModalProps> = ({ onClose, VideoUrl }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  return (
    <div
      className="modal-overlay"
      style={{
        background: "rgba(0, 0, 0, 0.7)",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          maxWidth: "800px",
          margin: "20vh auto",
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "10px",
        }}
      >
        <button
          className="close-button"
          onClick={onClose}
          style={{
            color: "#333",
            cursor: "pointer",
            position: "absolute",
            top: "-20px",
            right: "-20px",
            backgroundColor: "#fff",
            border: "none",
            fontSize: "1.5rem",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
          }}
        >
          &times;
        </button>
        <video
          ref={videoRef}
          src={VideoUrl}
          controls
          style={{
            maxWidth: "100%",
            maxHeight: "60vh",
            width: "auto",
            display: "block",
            margin: "0 auto",
          }}
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  );
};

const GifModal: React.FC<GifModalProps> = ({ onClose, SquallMap }) => {
  return (
    <div
      className="modal-overlay"
      style={{ background: "rgb(0,0,0,0.3)" }}
      onClick={onClose}
    >
      <div className="modal-content" style={{ display: "flex" }}>
        <img
          src={`${process.env.REACT_APP_BACKEND_IP}/api/getgif/${SquallMap}`}
          width={100}
          alt="GIF"
        />
        <CloseIcon
          onClick={() => onClose()}
          style={{ color: "white", cursor: "pointer", paddingLeft: "20px" }}
          fontSize="medium"
        />
      </div>
    </div>
  );
};

const Squall = () => {
  const windowWidth = useRef(window.innerWidth);
  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const data = useSelector((state: any) => state?.app);
  const [gif, setGif] = useState(false);
  const [file, setFile] = useState(false);
  const [jsonData, setJsonData] = useState<any>();
  const [error, setError] = useState();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [SquallMap, setSquallMap] = useState("");
  const [userLat, setUserLat] = useState(0);
  const [userLon, setUserLon] = useState(0);
  const [PrevLat, setPrevLat] = useState(0);
  const [PrevLon, setPrevLon] = useState(0);
  const [CurLat, setCurLat] = useState(0);
  const [CurLon, setCurLon] = useState(0);
  const [pinx, setPinX] = useState<any>();
  const [piny, setPinY] = useState<any>();
  const [pinPx, setPinPx] = useState<any>();
  const [pinPy, setPinPy] = useState<any>();
  const [pinCx, setPinCx] = useState<any>();
  const [pinCy, setPinCy] = useState<any>();
  const [init, setInit] = useState<any>(0);
  const [storm, setStorm] = useState<any>(0);
  const [intensity, setIntensity] = useState<any>("");
  const [fc, setFc] = useState<any>("");
  const mapWidth = 600;
  const mapHeight = 600;
  const [loading, setLoading] = useState(true);
  const [pinDiff, setPinDiff] = useState<any>();
  const [svgVisible, setSvgVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showNoDataMessage, setShowNoDataMessage] = useState(false);

  useEffect(() => {
    if (!loading) {
      const validData = jsonData?.some(
        (entry: any) =>
          entry["Squall Header"]?.trim() !== "" ||
          entry["Squall Advisory"]?.trim() !== ""
      );

      setShowNoDataMessage(!validData);
    }
  }, [loading, jsonData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Loading CSV data from API...");
        const fid = localStorage.getItem("fid") || "1001";

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_IP}/api/getcsv/${fid}`
        );

        if (response.status === 404) {
          setTitle("There are currently no severe warnings active for this location");
          setShowNoDataMessage(true);
        } else {
          const csvData = response.data;
          console.log("API data loaded successfully:", csvData[0]);

          setJsonData(csvData);

          setTitle(csvData[0]["Squall Header"] || "");
          setDesc(csvData[0]["Squall Advisory"] || "");
          setSquallMap(csvData[0]["SatImgSection"] || "");
          setUserLat(csvData[0]["Client's lat"] || 0);
          setUserLon(csvData[0]["Client's lon"] || 0);
          setCurLat(csvData[0]["Previous Sat lat"] || 0);
          setCurLon(csvData[0]["Previous Sat lon"] || 0);
          setPrevLat(csvData[0]["Latest Sat lat"] || 0);
          setPrevLon(csvData[0]["Latest Sat lon"] || 0);
          setInit(csvData[0]["Initial Heading"] || 0);
          setStorm(csvData[0]["Storm Speed"] || 0);
          setIntensity(csvData[0]["Squall Intensity"] || "");
          setFc(csvData[0]["Forecaster"] || "");
        }
      } catch (error: any) {
        console.error("Error loading API data:", error);
        setError(error.message || "An error occurred while loading data");
        if (error.response?.status === 404) {
          setShowNoDataMessage(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (SquallMap) {
      console.log("Calculating pin positions for:", SquallMap);

      const sectionIndex = section.indexOf(SquallMap);
      if (sectionIndex !== -1) {
        const north = degree[sectionIndex * 4 + 0];
        const south = degree[sectionIndex * 4 + 1];
        const west = degree[sectionIndex * 4 + 2];
        const east = degree[sectionIndex * 4 + 3];

        console.log(`Map bounds - North: ${north}, South: ${south}, West: ${west}, East: ${east}`);

        const latRange = north - south;
        const lonRange = east - west;
        const pixel_x = 600;
        const pixel_y = 600;

        const userPinX = ((userLon - west) / lonRange) * pixel_x;
        const userPinY = ((north - userLat) / latRange) * pixel_y;

        const prevPinX = ((CurLon - west) / lonRange) * pixel_x;
        const prevPinY = ((north - CurLat) / latRange) * pixel_y;

        const curPinX = ((PrevLon - west) / lonRange) * pixel_x;
        const curPinY = ((north - PrevLat) / latRange) * pixel_y;

        setPinX(userPinX);
        setPinY(userPinY);
        setPinCx(curPinX);
        setPinCy(curPinY);
        setPinPx(prevPinX);
        setPinPy(prevPinY);

        console.log(`Pin positions calculated:
          User: (${userPinX.toFixed(1)}, ${userPinY.toFixed(1)})
          Current Storm: (${curPinX.toFixed(1)}, ${curPinY.toFixed(1)})
          Previous Storm: (${prevPinX.toFixed(1)}, ${prevPinY.toFixed(1)})`);
      } else {
        console.warn(`Section ${SquallMap} not found in section array`);
      }
    }
  }, [CurLat, CurLon, PrevLat, PrevLon, SquallMap, userLat, userLon]);

  const closeModalVideo = () => {
    setFile(false);
  };

  const closeModalGif = () => {
    setGif(false);
  };

  useEffect(() => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: windowWidth.current > 1000 ? true : false,
    });
  }, []);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        if (SquallMap) {
          const fid = localStorage.getItem("fid") || "1001";
          console.log(`Fetching video for FID: ${fid}, SquallMap: ${SquallMap}`);
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_IP}/api/getvideo/${fid}/${SquallMap}/`,
            { responseType: "blob" }
          );
          const url = URL.createObjectURL(response.data);
          setVideoUrl(url);
        } else {
          console.warn("SquallMap is not available yet");
        }
      } catch (error) {
        console.error("Error fetching video:", error);
      }
    };
    fetchVideo();
  }, [SquallMap]);

  useEffect(() => {
    setTimeout(() => {
      setImageLoaded(true);
      setSvgVisible(true);
    }, 2000);
  }, []);

  useEffect(() => {
    setOpen(data.toggle);
  }, [data]);

  function render() {
    if (!jsonData || !Array.isArray(jsonData)) return null;
    return jsonData.map((elem: any, index: number) => {
      if (elem[`Date/Time Forecast`] === "") {
        return null;
      } else if (elem[`Position lat`] === "ALL CLEAR") {
        return (
          <tr key={index} className="sqtr">
            <td className="sqtd">No Squall</td>
            <td className="sqtd">-</td>
            <td className="sqtd">-</td>
            <td className="sqtd">-</td>
            <td className="sqtd">-</td>
          </tr>
        );
      } else {
        const formattedDateTime =
          elem[`Date/Time Forecast`].slice(0, 3) +
          elem[`Date/Time Forecast`].slice(10, 16);
        const positionLat = isNaN(parseFloat(elem[`Position lat`]))
          ? elem[`Position lat`]
          : parseFloat(elem[`Position lat`]).toFixed(1) + " N";
        const positionLon = isNaN(parseFloat(elem[`Position lon`]))
          ? elem[`Position lon`]
          : parseFloat(elem[`Position lon`]).toFixed(1) + " E";
        const timeToSiteInHours = parseFloat(elem[`Time to Site (hr)`]);
        let timeToSite;
        if (isNaN(timeToSiteInHours)) {
          timeToSite = elem[`Time to Site (hr)`];
        } else {
          const hours = Math.floor(timeToSiteInHours);
          const minutes = Math.round((timeToSiteInHours - hours) * 60);
          timeToSite = hours > 0 ? `${hours}hr ${minutes}min` : `${minutes}min`;
        }
        const distanceToSite = isNaN(parseFloat(elem[`Distance to Site (nm)`]))
          ? elem[`Distance to Site (nm)`]
          : Math.round(parseFloat(elem[`Distance to Site (nm)`])) + "NM";
        return (
          <tr key={index} className="sqtr">
            <td className="sqtd">{formattedDateTime}</td>
            <td className="sqtd">{positionLat}</td>
            <td className="sqtd">{positionLon}</td>
            <td className="sqtd">{timeToSite}</td>
            <td className="sqtd">{distanceToSite}</td>
          </tr>
        );
      }
    });
  }

  useEffect(() => {
    if (pinCx && pinPx && pinPy && pinCy) {
      let slope = (pinCy - pinPy) / (pinCx - pinPx);
      let y = pinPy + slope * (pinx - pinPx);
      setPinDiff(y);
    }
  }, [pinPy, pinCy, pinx, piny, pinPx, pinCx]);

  const handleImageLoad = () => {
    setSvgVisible(true);
    setImageLoaded(true);
    console.log("Image loaded successfully");
  };

  useEffect(() => {
    if (SquallMap) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_IP}api/getimg/${SquallMap}`, {
          responseType: "blob",
        })
        .then((response) => {
          if (response.status === 200) {
            setImageLoaded(true);
          } else {
            throw new Error("Network response was not ok.");
          }
        })
        .catch((error) => {
          console.error("Error fetching image:", error);
        });
    }
  }, [SquallMap]);

  return (
    <div className={open ? "sideNavOpen" : "sideNavClose"}>
      {!showNoDataMessage ? (
        <div>
          <Box className="fug-container bg-default flex sidenav-full">
            {loading ? (
              <div className={"loader-div1"}>
                <WeatherLoader />
              </div>
            ) : (
              <div className="content-wrap page-forecast">
                <div
                  className="discussion-content"
                  style={{
                    marginBottom: -30,
                    margin: "16px",
                    padding: "20px",
                    borderRadius: "20px",
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <div className="titleBox">
                        <div className="title">{title}</div>
                        <div className="description">
                          <p>{desc}</p>
                        </div>
                      </div>

                      <div className="map" style={{ overflowX: "auto" }}>
                        <div
                          style={{
                            position: "relative",
                            width: mapWidth,
                            height: mapHeight,
                            border: "2px solid #ccc",
                            borderRadius: "8px"
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_BACKEND_IP}/api/getimg/${SquallMap}`}
                            alt="Satellite Map"
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              visibility: svgVisible ? "visible" : "hidden",
                            }}
                            onLoad={handleImageLoad}
                          />

                          {imageLoaded && pinx && piny && (
                            <>
                              <div
                                style={{
                                  position: "absolute",
                                  left: pinx - 5,
                                  top: piny - 5,
                                  width: 10,
                                  height: 10,
                                  backgroundColor: "red",
                                  borderRadius: "50%",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  border: "2px solid red",
                                  left: pinx - 35,
                                  top: piny - 35,
                                  width: 70,
                                  height: 70,
                                  backgroundColor: "transparent",
                                  borderRadius: "50%",
                                }}
                              />

                              {pinPx && pinPy && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: pinPx - 5,
                                    top: pinPy - 5,
                                    width: 10,
                                    height: 10,
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    
                                  }}
                                  />
                              )}

                              {pinCx && pinCy && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: pinCx - 5,
                                    top: pinCy - 5,
                                    width: 10,
                                    height: 10,
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    zIndex: 5,// add this line
                                  }}
                                  />
                              )}

                              {svgVisible && pinCx && pinCy && pinPx && pinPy && (
                                <svg
                                  style={{ position: "absolute", left: "0px", top: "0px" }}
                                  width={mapWidth}
                                  height={mapHeight}
                                >
                                  <defs>
                                    <marker
                                      id="arrowhead"
                                      markerWidth="10"
                                      markerHeight="10"
                                      refX="8"
                                      refY="3"
                                      orient="auto"
                                    >
                                      <path d="M0,0 L0,6 L9,3 z" fill="red" />
                                    </marker>
                                    <marker
                                      id="arrowheadPrev"
                                      markerWidth="10"
                                      markerHeight="10"
                                      refX="8"
                                      refY="3"
                                      orient="auto"
                                    >
                                      <path d="M0,0 L0,6 L9,3 z" fill="white" />
                                    </marker>
                                  </defs>

                                  <path
                                    d={`M${pinCx},${pinCy} L${pinx},${piny}`}
                                    fill="none"
                                    stroke="red"
                                    strokeWidth="2"
                                    strokeDasharray="none"
                                    markerEnd="url(#arrowhead)"
                                  />

                                  <path
                                    d={`M${pinPx},${pinPy} L${pinCx},${pinCy}`}
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2"
                                    markerEnd="url(#arrowheadPrev)"
                                  />
                                </svg>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="tables-container" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        flexShrink: 0,
                        overflowX: 'auto'
                      }}>
                        <div className="tableBox margin-tableBox" style={{
                          minWidth: '800px',
                          maxWidth: '800px',
                          flexShrink: 0
                        }}>
                          <table className="table">
                            <thead className="tableheader">
                              <tr className="sqtr">
                                <th className="sqth">INITIAL HEADING</th>
                                <th className="sqth">STORM SPEED</th>
                                <th className="sqth">SQUALL INTENSITY</th>
                                <th className="sqth">FORECASTER</th>
                              </tr>
                            </thead>
                            <tbody className="tablebody">
                              <tr className="sqtr">
                                <td className="sqtd">
                                  {init ? Math.floor(init) : "-"}
                                </td>
                                <td className="sqtd">
                                  {storm ? Math.floor(storm) : "-"}
                                </td>
                                <td className="sqtd">
                                  {intensity ? intensity : "-"}
                                </td>
                                <td className="sqtd">{fc ? fc : "-"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="tableBox margin-tableBox" style={{
                          minWidth: '800px',
                          maxWidth: '800px',
                          flexShrink: 0
                        }}>
                          <table className="table">
                            <thead className="tableheader">
                              <tr className="sqtr">
                                <th className="sqth">Date/Time</th>
                                <th className="sqth" colSpan={2}>
                                  Position
                                </th>
                                <th className="sqth">Time to</th>
                                <th className="sqth">Distance to</th>
                              </tr>
                              <tr className="sqtr">
                                <th className="sqth">UTC+8</th>
                                <th className="sqth">LAT.</th>
                                <th className="sqth">LONG.</th>
                                <th className="sqth">Plant area</th>
                                <th className="sqth">Plant area</th>
                              </tr>
                            </thead>
                            <tbody className="tablebody">{render()}</tbody>
                          </table>
                        </div>
                      </div>
                      <div className="button">
                        <button
                          id="file"
                          onClick={() => setFile((prev: boolean) => !prev)}
                        >
                          MP4 animation (large)
                        </button>
                      </div>

                      {file && (
                        <ImageModal
                          onClose={closeModalVideo}
                          VideoUrl={videoUrl}
                        />
                      )}
                      {gif && (
                        <GifModal
                          onClose={closeModalGif}
                          SquallMap={SquallMap}
                        />
                      )}
                    </Grid>
                  </Grid>
                </div>
              </div>
            )}
          </Box>
        </div>
      ) : (
        <div className="noData">
          There are currently no severe warnings active for this location
        </div>
      )}
    </div>
  );
};

export default Squall;